<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'

const props = defineProps({
    title:    { type: String, required: true },
    count:    { type: Number, default: null },
    countVariant: { type: String, default: 'brand' },
    linkLabel:{ type: String, default: null },
    linkRoute:{ type: String, default: null },
    editMode: { type: Boolean, default: false },
    widgetId: { type: String, required: true },
})

const emit = defineEmits(['hide'])
</script>

<template>
    <div class="bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 rounded-2xl overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between gap-2.5 px-[18px] pt-[15px] pb-3">
            <h3 class="text-[14.5px] font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
                {{ title }}
                <span
                    v-if="count !== null"
                    class="text-[11px] font-bold px-2 py-0.5 rounded-full"
                    :class="{
                        'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400': countVariant === 'brand',
                        'bg-orange-50 dark:bg-orange-950 text-[#D9663A] dark:text-orange-400': countVariant === 'warn',
                    }"
                >{{ count }}</span>
            </h3>
            <div class="flex items-center gap-2">
                <Link
                    v-if="linkLabel && linkRoute"
                    :href="route(linkRoute)"
                    class="text-[12.5px] text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300"
                >{{ linkLabel }}</Link>

                <!-- Drag handle (visible in edit mode) -->
                <div
                    v-if="editMode"
                    class="drag-handle cursor-grab active:cursor-grabbing text-gray-300 dark:text-neutral-600 hover:text-gray-500 dark:hover:text-neutral-400 transition-colors"
                    :title="$t('dashboard.drag_handle')"
                >
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>
                </div>

                <!-- Hide button (visible in edit mode) -->
                <button
                    v-if="editMode"
                    class="w-6 h-6 rounded flex items-center justify-center text-gray-300 dark:text-neutral-600 hover:text-red-400 transition-colors"
                    :title="$t('dashboard.hide_widget')"
                    @click="emit('hide', widgetId)"
                >
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6l-12 12"/></svg>
                </button>
            </div>
        </div>

        <!-- Body -->
        <div class="px-[18px] pb-[18px]">
            <slot/>
        </div>
    </div>
</template>
